/**
 * 월간 보고서 공유 링크 — `/r?t=` (261030-report-auto-dispatch). 공개 화면이고 **휴대폰이 기준**이다.
 * 구독 기간 끝날 알림톡의 「월간 보고서 보기」 버튼이 연다. 로그인 없이 발송 때 고정한 보고서를 본다.
 *
 * ★ 토큰은 주소창에서 곧바로 지우고 그 탭의 sessionStorage 에만 둔다(결제 링크와 같다 — 인앱 브라우저가 다시 읽어도 이어진다).
 *   토큰은 **body 로만** 보낸다. 무효 토큰은 한 문장으로 답한다(서버도 사유를 가르지 않는다)
 */
import { reportDispatch } from '@geo-admin/shared/contract/api/index.ts';
import type { ReportPageView } from '@geo-admin/shared/contract/api/reportDispatch.ts';
import { useEffect, useRef, useState } from 'react';
import { LogoMark } from '../components/LogoMark.tsx';
import { ReportPage } from '../components/ReportPageView.tsx';
import { ApiError, call } from '../lib/call.ts';
import { useUrlToken } from '../lib/urlToken.ts';

const TOKEN_KEY = 'geo-report-share';
const LINK_FAILED = '보고서 링크가 만료됐거나 없습니다. 담당자에게 새 링크를 요청해 주세요.';

type State = { kind: 'loading' } | { kind: 'ok'; view: ReportPageView } | { kind: 'fail'; message: string };

export function ReportSharePage() {
  const token = useUrlToken(TOKEN_KEY);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      setState({ kind: 'fail', message: LINK_FAILED });
      return;
    }
    void call(reportDispatch.viewReportShare, { body: { token } })
      .then((view) => setState({ kind: 'ok', view }))
      .catch((e: Error) =>
        setState({
          kind: 'fail',
          message: e instanceof ApiError && e.code === 'rate-limited' ? e.message : LINK_FAILED,
        }),
      );
  }, [token]);

  if (state.kind === 'ok') return <ReportPage view={state.view} />;
  return (
    <div className="min-h-[100dvh] bg-slate-50 font-suit">
      <header className="flex h-14 items-center justify-center border-slate-200 border-b bg-white">
        <LogoMark tone="onLight" size="sm" />
      </header>
      <main className="mx-auto w-full max-w-md px-4 pt-16 text-center">
        {state.kind === 'loading' ? (
          <p className="text-slate-500 text-sm">보고서를 불러오는 중…</p>
        ) : (
          <>
            <p className="font-bold text-lg text-slate-900">보고서를 열 수 없습니다</p>
            <p className="mt-2 break-keep text-slate-600 text-sm leading-relaxed">{state.message}</p>
          </>
        )}
      </main>
    </div>
  );
}
